<script setup lang="ts">
import { licenseTier, type LicenseInput } from '~/types/licenses/tiers';

interface Props {
    /** License slug (or a `{ type|slug }` object) — resolved to a tier identity. */
    license: LicenseInput;
    /** Render the tier icon. */
    showIcon?: boolean;
    /** Render the tier name. */
    showLabel?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

const props = withDefaults(defineProps<Props>(), {
    showIcon: true,
    showLabel: true,
    size: 'md',
});

const tier = computed(() => licenseTier(props.license));
const iconSrc = computed(() => `/images/business/${tier.value.icon}`);
</script>

<template>
    <span class="license-badge" :class="[`license-badge--${tier.hue}`, `license-badge--${size}`]">
        <img v-if="showIcon" :src="iconSrc" :alt="tier.name" class="license-badge__icon" />
        <span v-if="showLabel" class="license-badge__label">{{ tier.name }}</span>
    </span>
</template>

<style scoped>
/* The badge recipe: a wash, its ink, a pill, and no border. The tiers take the
   design system's own plan tokens, so a business seat and the consumer seat of the
   same rank read as one identity. (The hex ramp this replaced was self-contained
   because the old business tokens were scoped to `.business-portal`; these live on
   :root, so a teleported dialog resolves them too.) */
.license-badge {
    display: inline-flex;
    align-items: center;
    border-radius: var(--radius-full);
    font-family: var(--font-sans);
    font-weight: var(--weight-medium);
    line-height: var(--leading-xs);
    white-space: nowrap;
}

.license-badge__icon {
    display: block;
    flex-shrink: 0;
}

/* Ascending rank: Signature, Pro, Essentials, then the entry seat below the ladder. */
.license-badge--violet {
    background: var(--tier-signature-wash);
    color: var(--tier-signature-ink);
}

.license-badge--blue {
    background: var(--tier-pro-wash);
    color: var(--tier-pro-ink);
}

.license-badge--teal {
    background: var(--tier-essentials-wash);
    color: var(--tier-essentials-ink);
}

.license-badge--slate {
    background: var(--neutral-wash);
    color: var(--neutral-ink);
}

.license-badge--sm {
    gap: var(--space-1);
    padding: var(--space-0-5) var(--space-2);
    font-size: var(--text-xs);
}

.license-badge--sm .license-badge__icon {
    width: var(--icon-sm);
    height: var(--icon-sm);
}

.license-badge--md {
    gap: var(--space-1-5);
    padding: var(--space-1) var(--space-2-5);
    font-size: var(--text-sm);
    line-height: var(--leading-sm);
}

.license-badge--md .license-badge__icon {
    width: var(--icon-md);
    height: var(--icon-md);
}

.license-badge--lg {
    gap: var(--space-2);
    padding: var(--space-1-5) var(--space-3);
    font-size: var(--text-base);
    line-height: var(--leading-base);
}

.license-badge--lg .license-badge__icon {
    width: 28px;
    height: 28px;
}
</style>
